import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Bell, Moon, BellRing } from 'lucide-react';
import { toast } from 'sonner';
import { pushClient } from '../utils/pushClient';

/**
 * <PushAndQuietHoursCard /> — Iter-181 Messaging Slice 3.
 *
 * Drop-in card for any /settings page. Wraps:
 *   • Browser push enable/disable + permission UX
 *   • Quiet hours window (start/end + timezone)
 *   • Per-event push opt-in (messages, announcements, emergency override)
 */
const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
  'Europe/London', 'Europe/Berlin', 'Asia/Tokyo', 'Australia/Sydney', 'UTC',
];

const PushAndQuietHoursCard = () => {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState('default');
  const [enabled, setEnabled] = useState(false);
  const [working, setWorking] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    const isSupp = pushClient.isSupported();
    setSupported(isSupp);
    if (isSupp) setPermission(pushClient.permission());
    pushClient.isEnabled().then(setEnabled).catch(() => {});
    pushClient.getPreferences().then(setPrefs).catch(() => {});
  }, []);

  const handleEnable = async () => {
    setWorking(true);
    try {
      await pushClient.enable();
      setEnabled(true);
      setPermission('granted');
      toast.success('Push notifications enabled');
    } catch (e) {
      toast.error(e?.message || 'Failed to enable');
    } finally {
      setWorking(false);
    }
  };

  const handleDisable = async () => {
    setWorking(true);
    try {
      await pushClient.disable();
      setEnabled(false);
      toast.success('Push notifications disabled');
    } catch (e) {
      toast.error(e?.message || 'Failed to disable');
    } finally {
      setWorking(false);
    }
  };

  const handleSendTest = async () => {
    try {
      await pushClient.sendTest();
      toast.success('Test push sent — check your device');
    } catch {
      toast.error('Test push failed');
    }
  };

  const updatePref = async (patch) => {
    if (!prefs) return;
    const next = { ...prefs, ...patch };
    setPrefs(next);
    setSavingPrefs(true);
    try {
      await pushClient.updatePreferences(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
      setPrefs(prefs);
    } finally {
      setSavingPrefs(false);
    }
  };

  if (!supported) {
    return (
      <Card className="bg-white border-gray-200" data-testid="push-quiet-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Bell className="w-5 h-5 text-cyan-600" />
            Push Notifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">
            Your browser doesn't support push notifications. Try Chrome, Edge, Firefox, or
            Safari 16.4+ on iOS.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-gray-200" data-testid="push-quiet-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <BellRing className="w-5 h-5 text-cyan-600" />
          Push Notifications & Quiet Hours
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enable / disable */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-gray-900">Browser push</p>
            <p className="text-xs text-gray-500">
              {permission === 'denied'
                ? 'Permission denied. Re-enable in your browser settings.'
                : enabled
                  ? 'Active on this device'
                  : 'Get instant alerts on this device'}
            </p>
          </div>
          {enabled ? (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSendTest}
                data-testid="push-send-test-btn"
              >
                Send test
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDisable}
                disabled={working}
                data-testid="push-disable-btn"
              >
                Disable
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              onClick={handleEnable}
              disabled={working || permission === 'denied'}
              data-testid="push-enable-btn"
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
            >
              Enable
            </Button>
          )}
        </div>

        {/* Per-event opt-in */}
        {prefs && (
          <div className="space-y-3 border-t pt-4" data-testid="push-event-prefs">
            <p className="text-xs uppercase tracking-wide text-gray-500">Notify me about</p>
            {[
              { key: 'push_messages',      label: 'Direct messages',          desc: 'Replies and chat from your team' },
              { key: 'push_announcements', label: 'Client announcements',     desc: 'Important shift-wide updates' },
              { key: 'push_emergency',     label: 'Emergency channel',        desc: 'Always alerts (overrides quiet hours)' },
              { key: 'push_typing',        label: 'Typing indicators',        desc: 'Off by default — can be noisy' },
            ].map(({ key, label, desc }) => (
              <div key={key} className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-900">{label}</p>
                  <p className="text-xs text-gray-500">{desc}</p>
                </div>
                <Switch
                  checked={!!prefs[key]}
                  onCheckedChange={(v) => updatePref({ [key]: v })}
                  data-testid={`push-pref-${key}`}
                  disabled={savingPrefs}
                />
              </div>
            ))}
          </div>
        )}

        {/* Quiet hours */}
        {prefs && (
          <div className="space-y-3 border-t pt-4" data-testid="quiet-hours-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Moon className="w-4 h-4 text-violet-500" />
                <p className="text-sm font-medium text-gray-900">Quiet hours</p>
              </div>
              <Switch
                checked={!!prefs.quiet_enabled}
                onCheckedChange={(v) => updatePref({ quiet_enabled: v })}
                data-testid="quiet-hours-toggle"
                disabled={savingPrefs}
              />
            </div>
            {prefs.quiet_enabled && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-gray-600">Start hour</Label>
                  <select
                    className="w-full mt-1 border rounded-md px-2 py-1.5 text-sm"
                    value={prefs.quiet_start}
                    onChange={(e) => updatePref({ quiet_start: parseInt(e.target.value, 10) })}
                    data-testid="quiet-start-select"
                  >
                    {Array.from({ length: 24 }, (_, h) => (
                      <option key={h} value={h}>{`${h.toString().padStart(2, '0')}:00`}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-gray-600">End hour</Label>
                  <select
                    className="w-full mt-1 border rounded-md px-2 py-1.5 text-sm"
                    value={prefs.quiet_end}
                    onChange={(e) => updatePref({ quiet_end: parseInt(e.target.value, 10) })}
                    data-testid="quiet-end-select"
                  >
                    {Array.from({ length: 24 }, (_, h) => (
                      <option key={h} value={h}>{`${h.toString().padStart(2, '0')}:00`}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Timezone</Label>
                  <select
                    className="w-full mt-1 border rounded-md px-2 py-1.5 text-sm"
                    value={prefs.timezone}
                    onChange={(e) => updatePref({ timezone: e.target.value })}
                    data-testid="quiet-tz-select"
                  >
                    {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                  </select>
                </div>
              </div>
            )}
            <p className="text-[11px] text-gray-500">
              {prefs.quiet_enabled
                ? `No push between ${String(prefs.quiet_start).padStart(2, '0')}:00 and ${String(prefs.quiet_end).padStart(2, '0')}:00 ${prefs.timezone}. Emergency channel still alerts.`
                : 'Quiet hours disabled — you\'ll receive all enabled push notifications.'}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PushAndQuietHoursCard;
